import { useMemo, useState } from "react";
import { films, filmRegions } from "@/mocks/films";
import { genres, genreIntro } from "@/mocks/siteContent";
import SectionHeading from "./SectionHeading";
import FilmCard from "./FilmCard";

export default function GenreSection() {
  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [query, setQuery] = useState("");

  const keyword = query.trim();

  const filtered = useMemo(() => {
    return films.filter((film) => {
      const matchGenre = genre === "全部" || film.genre === genre;
      const matchRegion = region === "全部" || film.region === region;
      const matchQuery =
        keyword === "" ||
        film.title.includes(keyword) ||
        film.director.includes(keyword) ||
        film.tags.some((tag) => tag.includes(keyword));
      return matchGenre && matchRegion && matchQuery;
    });
  }, [genre, region, keyword]);

  const reset = () => {
    setGenre("全部");
    setRegion("全部");
    setQuery("");
  };

  return (
    <section id="genres" className="w-full bg-background-50 px-4 py-14 md:px-8 md:py-20">
      <div className="mx-auto max-w-6xl">
        <SectionHeading
          eyebrow="按题材浏览"
          title="切换题材与地区，只看你感兴趣的伦理片"
          desc="下面的筛选只作用于本页已经展示的内容。先用题材标签圈定方向，再用地区缩小范围，最后可以用关键词搜片名、导演或标签，随时可以一键恢复全部。"
        />

        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="题材筛选">
            {genres.map((item) => {
              const isActive = genre === item;
              return (
                <button
                  key={item}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setGenre(item)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-foreground-950"
                  }`}
                >
                  {item}
                </button>
              );
            })}
          </div>

          <label className="relative flex w-full items-center lg:w-72">
            <i className="ri-search-line pointer-events-none absolute left-3 text-base text-foreground-400"></i>
            <span className="sr-only">搜索片名、导演或标签</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索片名 / 导演 / 标签"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-1.5">
          <span className="mr-1 font-label text-xs text-foreground-500">地区</span>
          {filmRegions.map((item) => {
            const isActive = region === item;
            return (
              <button
                key={item}
                type="button"
                onClick={() => setRegion(item)}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                  isActive
                    ? "bg-secondary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-600 hover:border-secondary-400 hover:text-foreground-900"
                }`}
              >
                {item}
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex flex-col gap-2 border-l-2 border-primary-300 bg-background-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm leading-relaxed text-foreground-700">{genreIntro[genre]}</p>
          <span className="shrink-0 font-label text-xs text-foreground-500">共 {filtered.length} 部结果</span>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((film) => (
              <FilmCard key={film.id} film={film} />
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-base text-foreground-950">没有找到符合条件的条目</h3>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              试试换一个题材或地区，或者把关键词改得更短一些，比如只输入片名里的两个字。
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              恢复全部内容
            </button>
          </div>
        )}
      </div>
    </section>
  );
}